import { Workbook, Worksheet } from "exceljs";
import moment from "moment";
const ExcelJS = require("exceljs"); //eslint-disable-line

export class Indexes {
  worksheet: Worksheet;
  x: number;
  y: number;
  dx: number;
  dy: number;

  constructor(worksheet) {
    this.worksheet = worksheet;
    this.x = 0;
    this.y = 1;
    this.dx = 0;
    this.dy = 0;
  }

  header() {
    this.fill(`081D3A`);
    this.fontSize(18);
    this.bold();
    this.centerText();
    this.color({ argb: `FFFFFFFF` });
    this.height(20);
  }

  centerText() {
    this.getCell().alignment = {
      vertical: "middle",
      horizontal: "center",
      wrapText: true,
    };
  }

  wrapText() {
    this.getCell().alignment = {
      vertical: "top",
      horizontal: "left",
      wrapText: true,
    };
  }

  getCell() {
    return this.worksheet.getCell(this.cellID());
  }

  get() {
    return this.worksheet.getCell(this.cellID()).value;
  }

  dollar() {
    this.getCell().numFmt = '"$"#,##0.00;[Red]-"$"#,##0.00';
  }

  percentage() {
    this.getCell().numFmt = "0.00%";
  }

  text(value) {
    this.getCell().value = value;
  }

  formula(value) {
    this.getCell().value = { formula: value, date1904: false };
  }

  richText(value) {
    this.getCell().value = { richText: value };
  }

  getRow() {
    return this.y;
  }

  getColumn() {
    return excelColumnNumberToName(this.x);
  }

  getX() {
    return this.x;
  }

  getY() {
    return this.y;
  }

  goToColumn(column) {
    this.x = column;
  }

  columnRelativeMove(value) {
    this.x += value;
  }

  nextRow() {
    this.y++;
  }

  goToRow(row) {
    this.y = row;
  }

  rowRelativeMove(value) {
    this.y += value;
  }

  merge() {
    this.worksheet.mergeCells(this.selectionID());
  }

  height(value) {
    this.worksheet.getRow(this.y).height = value;
  }

  width(value) {
    this.worksheet.getColumn(excelColumnNumberToName(this.x)).width = value;
  }

  bold() {
    this.getCell().font = {
      ...this.getCell().font,
      bold: true,
    };
  }

  italic() {
    this.getCell().font = {
      ...this.getCell().font,
      italic: true,
    };
  }

  fontSize(value) {
    this.getCell().font = {
      ...this.getCell().font,
      size: value,
    };
  }

  color(value) {
    this.getCell().font = {
      ...this.getCell().font,
      color: value,
    };
  }

  previousRow() {
    if (this.y > 0) this.y--;
  }

  nextColumn() {
    this.x++;
  }

  previousColumn() {
    if (this.x > 0) this.x--;
  }

  select(dx, dy) {
    this.dx = dx;
    this.dy = dy;
  }

  unselect() {
    this.dx = 0;
    this.dy = 0;
  }

  cellID() {
    return `${excelColumnNumberToName(this.x)}${this.y}`;
  }

  relativeCellID(dx, dy) {
    return `${excelColumnNumberToName(this.x + dx)}${this.y + dy}`;
  }

  selectionID() {
    return `${excelColumnNumberToName(this.x)}${
      this.y
    }:${excelColumnNumberToName(this.x + this.dx)}${this.y + this.dy}`;
  }

  fill(color: string) {
    fillWithColor(this.selectionID(), color, this.worksheet);
  }

  rectangle() {
    rectangleBorder(this.selectionID(), this.worksheet);
  }

  underline() {
    bottomBorder(this.selectionID(), this.worksheet);
  }
}

export function fillWithColor(boundaries: string, color: string, worksheet) {
  const split = boundaries.split(":");
  const xs =
    excelColumnNameToNumber(split[0].match(/[A-Z]+/)[0].toUpperCase()) - 1;
  const xe =
    excelColumnNameToNumber(split[1].match(/[A-Z]+/)[0].toUpperCase()) - 1;
  const ys = parseInt(split[0].match(/\d+/)[0]);
  const ye = parseInt(split[1].match(/\d+/)[0]);

  for (let x = xs; x <= xe; x++) {
    for (let y = ys; y <= ye; y++) {
      worksheet.getCell(`${excelColumnNumberToName(x)}${y}`).fill = {
        ...worksheet.getCell(`${excelColumnNumberToName(x)}${y}`).fill,
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: color },
      };
    }
  }
}

export function rectangleBorder(boundaries, worksheet) {
  const split = boundaries.split(":");
  const xs =
    excelColumnNameToNumber(split[0].match(/[A-Z]+/)[0].toUpperCase()) - 1;
  const xe =
    excelColumnNameToNumber(split[1].match(/[A-Z]+/)[0].toUpperCase()) - 1;
  const ys = parseInt(split[0].match(/\d+/)[0]);
  const ye = parseInt(split[1].match(/\d+/)[0]);

  for (let x = xs; x <= xe; x++) {
    worksheet.getCell(`${excelColumnNumberToName(x)}${ys}`).border = {
      ...worksheet.getCell(`${excelColumnNumberToName(x)}${ys}`).border,
      top: { style: "thin" },
    };
    worksheet.getCell(`${excelColumnNumberToName(x)}${ye}`).border = {
      ...worksheet.getCell(`${excelColumnNumberToName(x)}${ye}`).border,
      bottom: { style: "thin" },
    };
    if (x == xs || x == xe) {
      for (let y = ys; y <= ye; y++) {
        const cellBorders = worksheet.getCell(
          `${excelColumnNumberToName(x)}${y}`
        ).border;
        worksheet.getCell(`${excelColumnNumberToName(x)}${y}`).border = {
          ...cellBorders,
          left: x === xs ? { style: "thin" } : undefined,
          right: x === xe ? { style: "thin" } : undefined,
        };
      }
    }
  }
}

export function bottomBorder(boundaries, worksheet) {
  const split = boundaries.split(":");
  const xs =
    excelColumnNameToNumber(split[0].match(/[A-Z]+/)[0].toUpperCase()) - 1;
  const xe =
    excelColumnNameToNumber(split[1].match(/[A-Z]+/)[0].toUpperCase()) - 1;
  const ye = parseInt(split[1].match(/\d+/)[0]);

  for (let x = xs; x <= xe; x++) {
    worksheet.getCell(`${excelColumnNumberToName(x)}${ye}`).border = {
      ...worksheet.getCell(`${excelColumnNumberToName(x)}${ye}`).border,
      bottom: { style: "thin" },
    };
  }
}

export const createWorkbook = () => {
  const workbook = new ExcelJS.Workbook();
  return workbook;
};

export const addWorksheet = (workbook: Workbook, name?: string) => {
  const worksheet = workbook.addWorksheet(name, {
    pageSetup: {
      paperSize: undefined,
      fitToPage: true,
      fitToWidth: 1,
      blackAndWhite: true,
      horizontalCentered: true,
      fitToHeight: 1,
    },
  });
  worksheet.pageSetup.margins = {
    left: 0.5,
    right: 0.5,
    top: 0,
    bottom: 0.5,
    header: 0,
    footer: 0.3,
  };

  return worksheet;
};

export const rhapsodyHeader = (
  workbook: Workbook,
  worksheet: Worksheet,
  indexes: Indexes
) => {
  const RhapsodyImage = workbook.addImage({
    base64: RhapsodyLogoBase64,
    extension: "png",
  });
  worksheet.addImage(RhapsodyImage, {
    tl: { col: 0, row: 0 },
    ext: { width: 135, height: 40 },
  });
  indexes.width(20);
  indexes.nextColumn();
  indexes.text("Exported on Rhapsody.la");
  // indexes.width(35);
  indexes.bold();
  indexes.fontSize(18);
  indexes.nextRow();
  indexes.text(`Date: ${moment().format("lll")}`);
  indexes.nextRow();
  indexes.nextRow();
  indexes.previousColumn();
  indexes.nextRow();
};

export const downloadExcel = (workbook: any, fileName: string) => {
  workbook.xlsx
    .writeBuffer({
      base64: true,
    })
    .then(function (xls64) {
      // build anchor tag and attach file (works in chrome)
      const a = document.createElement("a");
      const data = new Blob([xls64], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      const url = URL.createObjectURL(data);
      a.href = url;
      a.download = `${fileName}.xlsx`;
      document.body.appendChild(a);
      a.click();
      setTimeout(function () {
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      }, 0);
    })
    .catch(function (error) {
      console.log(error.message);
    });
};

export const setColumns = <T>(
  exportColDef: ExportColDef[],
  items: Array<T>,
  worksheet: Worksheet
) => {
  const indexes = new Indexes(worksheet);

  exportColDef.forEach((c) => {
    if (c.width) indexes.width(c.width);
    indexes.rectangle();
    indexes.text(c.headerName);
    indexes.fontSize(14);
    indexes.bold();
    indexes.centerText();
    indexes.fill("f5f5f5");
    const back = indexes.getRow();
    items.forEach((i) => {
      indexes.nextRow();
      if (c.valueGetter) indexes.text(c.valueGetter(i[c.field]));
      if (c.formulaGetter) indexes.formula(c.formulaGetter(indexes));
      if (c.center) indexes.centerText();
      if (c.dollar) indexes.dollar();
      if (c.percentage) indexes.percentage();
      indexes.rectangle();
    });
    if (c.total) {
      indexes.nextRow();
      indexes.formula(
        `SUM(${indexes.getColumn()}${
          indexes.getRow() - items.length
        }:${indexes.getColumn()}${indexes.getRow() - 1})`
      );
      indexes.header();
      if (c.center) indexes.centerText();
      if (c.dollar) indexes.dollar();
    }
    indexes.goToRow(back);
    indexes.nextColumn();
  });
};

export type ExportColDef = {
  field?: string;
  headerName?: string;
  width?: number;
  center?: boolean;
  valueGetter?: (a: any) => string | number;
  formulaGetter?: (a: any) => string;
  dollar?: boolean;
  percentage?: boolean;
  total?: boolean;
};

export function camelCaseToHumanReadable(camelCaseString) {
  if (typeof camelCaseString !== "string") {
    console.error("Expected a string as an argument.");
    return;
  }

  // Replace uppercase letters with a space and the same letter in lowercase
  const withSpaces = camelCaseString.replace(/([A-Z])/g, " $1");

  // Make the first letter of the sentence uppercase and the rest of the sentence lowercase
  const capitalized =
    withSpaces.charAt(0).toUpperCase() + withSpaces.slice(1).toLowerCase();

  return capitalized;
}

export const RhapsodyLogoBase64 = `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`;

function excelColumnNameToNumber(columnName: string) {
  let columnNumber = 0;
  const base = "A".charCodeAt(0) - 1; // ASCII code of 'A' minus 1

  for (let i = 0; i < columnName.length; i++) {
    const char = columnName.charAt(i);
    columnNumber = columnNumber * 26 + (char.charCodeAt(0) - base);
  }
  return columnNumber;
}

function excelColumnNumberToName(columnNumber) {
  let _columnNumber = columnNumber;
  let columnName = "";
  while (_columnNumber >= 0) {
    const remainder = _columnNumber % 26;
    columnName = String.fromCharCode(65 + remainder) + columnName;
    _columnNumber = Math.floor(_columnNumber / 26) - 1;
  }
  return columnName;
}
export const alphabet = "ABCDEFGHIJKLMNOQRSTUVWXYZ";
export const alphabetArray = [
  "A",
  "B",
  "C",
  "D",
  "E",
  "F",
  "G",
  "H",
  "I",
  "J",
  "K",
  "L",
  "M",
  "N",
  "O",
  "P",
  "Q",
  "R",
  "S",
  "T",
  "U",
  "V",
  "W",
  "X",
  "Y",
  "Z",
];
